import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { invalidateComplianceReview } from "@/lib/hooks/use-compliance";
import { flowInstanceQueryKeys } from "@/lib/hooks/use-flow-instances";

/**
 * Everything a process transition can change, in one place.
 *
 * QL-399 gave the flow header its own reopen, which runs the same
 * `draftInstance` the process page runs — and the two invalidated different
 * sets, so reopening from the header left the compliance approvals queue
 * showing a request that had just been withdrawn. Rather than copy the list,
 * both surfaces call this.
 *
 * Lives in its own module because `use-compliance` imports
 * `use-flow-instances`; putting this in either would close a cycle.
 */
export function useInvalidateFlowProcess(instanceId: string | null, clientId: string | null) {
  const queryClient = useQueryClient();

  return useCallback(() => {
    // The instance subtree: status, metadata, process state, goals, …
    queryClient.invalidateQueries({ queryKey: flowInstanceQueryKeys.instance(instanceId) });
    // Withdrawing a pending approval moves this queue, and it lives outside
    // every instance key.
    queryClient.invalidateQueries({ queryKey: ["compliance", "approvals"] });
    // Completion creates/relinks accounts, creates orders (one-time and
    // recurring), writes savings-plan placeholders, and touches goal linkage.
    for (const tab of [
      "accounts",
      "orders",
      "recurring-orders",
      // QL-375 group 9 — completion writes mandate orders and the placeholder.
      "mandate-orders",
      "mandates",
      "savings-plans",
      "goals",
      // The client's Flows tab shows a status per instance.
      "flowInstances",
      // QL-275 — every transition appends a flow_instance_event.
      "activityEvents",
    ]) {
      queryClient.invalidateQueries({ queryKey: ["clients", clientId, tab] });
    }
    // QL-21 — reaching `completed` writes the client's screening row, moving
    // the Clients review, and every step moves the status the Flows review
    // shows. With a 60s staleTime and refetchOnWindowFocus off, a compliance
    // user with either page open would otherwise keep the stale rows.
    invalidateComplianceReview(queryClient);
  }, [queryClient, instanceId, clientId]);
}
